<template>
  <div class="topbar" :class="roleClass">
    <div class="topbar_body">
      <div class="first text-white d-flex align-items-center" :class="roleClass">
        <div class="left">
          <button
            v-if="!leftButtons.length"
            type="button"
            class="btn-link-menu hideOnBigSize"
            style="--hide-on-big-display: inline-flex"
            @click="$emit('toggle-menu')"
          >
            <span class="material-icons">menu</span>
          </button>
          <div v-else>
            <button
              v-for="(button, index) in leftButtons"
              :key="`left-${index}`"
              type="button"
              class="btn-link-menu"
              @click="button.action?.()"
            >
              <span class="material-icons">{{ button.icon }}</span>
            </button>
          </div>
        </div>

        <div class="center" v-if="title">
          <div class="topbar__title">{{ title }}</div>
        </div>

        <div class="right">
          <div v-if="rightButtons.length" class="d-flex">
            <button
              v-for="(button, index) in rightButtons"
              :key="`right-${index}`"
              type="button"
              class="btn-link-menu"
              @click="button.action?.()"
            >
              <span class="material-icons" v-if="button.icon">{{ button.icon }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="subs.length"
      class="menu hideOnBigSize"
      :class="[roleClass, { 'menu--ios-cordova': isIosCordova }]"
      style="--hide-on-big-display: flex"
    >
      <div class="menu__body ml-2 mr-2">
        <RouterLink
          v-for="(sub, index) in mobileSubs"
          :key="`sub-${index}`"
          :to="sub.to"
          class="link__body"
        >
          <span class="material-icons d-flex align-items-center">{{ sub.icon }}</span>
          <span class="icon__body__text">{{ downmenuLabel(sub.num) }}</span>
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useLangStore } from '@/stores/lang';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';

defineEmits(['toggle-menu']);

const route = useRoute();
const langStore = useLangStore();
const topbarStore = useTopbarStore();
const viewerStore = useViewerStore();
viewerStore.initFromStorage();
const isIosCordova =
  import.meta.env.VITE_APP_PLATFORM === 'cordova' &&
  import.meta.env.VITE_CORDOVA_PLATFORM === 'ios';

const subs = computed(() => {
  const getter = route.meta?.subtopbar;
  if (typeof getter === 'function') {
    const resolved = getter();
    return Array.isArray(resolved) ? resolved : [];
  }
  return Array.isArray(getter) ? getter : [];
});

const roleClass = computed(() => {
  const role = viewerStore.role;
  if (role === 'child') return 'bg-pupil';
  return 'bg-parent';
});
const mobileSubs = computed(() =>
  subs.value.filter((sub) => !sub.menubarOnly && (!sub.only || (sub.only === 'child' && viewerStore.isChild)))
);

const title = computed(() => {
  if (topbarStore.title) return topbarStore.title;
  if (route.name === 'feed') return langStore.translations?.feed || '';
  return '';
});

const leftButtons = computed(() => topbarStore.left);
const rightButtons = computed(() => topbarStore.right);

function downmenuLabel(index) {
  return langStore.translations?.downmenu?.[index] || '';
}

watch(
  () => route.fullPath,
  () => {
    topbarStore.clear();
    topbarStore.setSub(subs.value.length);
  },
  { immediate: true }
);
</script>

<style scoped>
.topbar__title {
  font-family: Roboto, sans-serif;
  font-style: normal;
  font-weight: 500;
}

.icon__body__text{
  font-size: 12px;
}

@media screen and (max-width: 768px) {
  .topbar__title {
    font-size: 18px;
  }
}

@media screen and (min-width: 768px) {
  .topbar__title {
    font-size: 24px;
    margin-left: 24px;
  }
}

.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

@media screen and (min-width: 768px) {
  .topbar {
    display: flex;
  }
}

@media screen and (min-width: 768px) {
  .topbar_body {
    width: 100%;
  }
}

.first {
  height: 50px;
}

@media screen and (min-width: 768px) {
  .first {
    height: 56px;
    width: min(1060px, calc(100vw - 15% - 260px - 10%));
    margin-left: calc(15% + 260px + 24px);
  }
}

.center {
  margin: 0 auto 0 0;
  height: 50px;
  text-align: left;
  display: flex;
  align-items: center;
}

.left {
  position: relative;
}

.right {
  position: relative;
}

@media screen and (max-width: 950px) {
  .right {
    display: block;
  }
}

@media screen and (min-width: 950px) {
  .right {
    display: none;
  }
}

.btn-link-menu {
  height: 50px;
  width: 50px;
  border: none;
  background: transparent;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-link-menu .material-icons {
  font-size: 24px;
}

@media screen and (max-width: 767px) {
  .first,
  .center,
  .btn-link-menu {
    height: 56px;
  }

  .btn-link-menu {
    width: 56px;
  }
}

.menu {
  position: fixed;
  height: 60px;
  bottom: 0;
  background: #363434;
  color: #7b7b7b;
  display: flex;
  align-items: center;
  justify-content: space-around;
  z-index: 100;
}

.menu--ios-cordova {
  height: 82px;
  padding-bottom: 8px;
  padding-inline: 16px;
}

@media screen and (max-width: 768px) {
  .menu {
    width: 100%;
  }
}

@media screen and (min-width: 768px) {
  .menu {
    display: none;
  }
}

.menu__body {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.menu__body .router-link-exact-active span {
  color: #fff !important;
}

.link__body {
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-direction: column;
}

.link__body:hover,
.link__body:active {
  text-decoration: none;
}

.link__body span {
  color: #bdbdbd;
  text-align: center;
  justify-content: center;
}

.bg-parent {
  background: #1c3043;
}

.bg-pupil {
  background: #164922;
}
</style>
